import { createElement } from 'react'
import { BarChart3 } from 'lucide-react'
import type { ParameterDefinition, UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import { ReportRunSection } from '../../components/nodes/trade-run-section'

/**
 * The Run Settings node (type `report`, renamed 2026-10-28 so "Report" means
 * only the results page): the end of the strategy's chain, after the Trade
 * Manager. It says how the strategy is RUN (sizing mode, starting cash, and for
 * Portfolio runs the commission and slippage the Report applies), carries the
 * Run button below them (back from the header 2026-10-28), and shows the newest
 * run with a way into its report.
 *
 * None of it is trading logic, which is why it is not on the Trade Manager:
 * the engine never sees this node (utils/editor-only-nodes.ts strips it and
 * its wire), and its settings stay out of the preview signature
 * (hooks/use-graph-signature.ts). The wire from the Trade Manager is the
 * chain's picture, not a dependency: Run reads the settings off this node
 * whether or not it is wired, so the input is optional.
 * See docs/superpowers/plans/2026-10-05-backtest-report-node-compact-tm-plan.md.
 */

export const REPORT_TYPE = 'report'

const parameters: ParameterDefinition[] = [
  {
    name: 'sizingMode',
    displayName: 'Sizing',
    type: 'select',
    default: 'cash',
    options: [
      { value: 'cash', label: 'Engine cash' },
      { value: 'unit', label: 'Portfolio' },
    ],
  },
  {
    name: 'initialCash',
    displayName: 'Starting Cash',
    type: 'float',
    default: 100000,
    min: 1,
  },
  {
    // Commission and slippage are the Report allocator's costs, which apply
    // to Portfolio-sized runs only; the engine has no commission input.
    name: 'commissionPerShare',
    displayName: 'Commission / Share',
    type: 'float',
    default: 0,
    min: 0,
    step: 0.001,
    dependsOn: { parameter: 'sizingMode', values: ['unit'] },
  },
  {
    name: 'slippageBps',
    displayName: 'Slippage (bps)',
    type: 'float',
    default: 0,
    min: 0,
    step: 0.5,
    dependsOn: { parameter: 'sizingMode', values: ['unit'] },
  },
]

/** The Report node parameters that say how a run is launched, never how it trades. */
export const RUN_SETTING_NAMES = [
  'sizingMode',
  'initialCash',
  'commissionPerShare',
  'slippageBps',
] as const

export const reportSchema: UniversalNodeSchema = {
  type: REPORT_TYPE,
  // Says how a run is launched; the engine never sees it (schemas/compound.ts).
  compound: {
    engine: 'editor-only',
    singleton: {
      code: 'MULTIPLE_REPORTS',
      noun: 'Run Settings node',
      plural: 'Run Settings nodes',
    },
    catalog: { ai: false, edgescript: false },
    runSettings: RUN_SETTING_NAMES,
  },
  displayName: 'Run Settings',
  category: 'debug',
  icon: BarChart3,
  description: 'How a run is sized: sizing mode, starting cash, commission and slippage.',
  aliases: ['run', 'results', 'backtest'],
  inputHandler: { type: 'single' },
  inputs: [
    {
      id: 'trades',
      displayName: 'Trades',
      acceptedTypes: ['array'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['tradeManager'],
      optional: true,
      // The full-width source control, not the old connection pill; optional,
      // so unwired it is a quiet prompt (Chris, 2026-10-28).
      control: 'source',
      emptyText: 'Connect the Trade Manager',
    },
  ],
  outputs: [],
  parameters,
  face: { shortName: 'Run Settings' },
  ui: {
    customSections: [
      {
        type: 'custom',
        position: 'after-parameters',
        render: () => createElement(ReportRunSection),
      },
    ],
  },
}

export function registerReportSchema(): void {
  UniversalNodeRegistry.register(reportSchema)
}
